/**
 * Text wrap around page objects (InDesign Text Wrap): the free segments ("slots") of each line of a text frame that
 * other objects of the page cut. A slot is one line for the wrap: beside an object that lets text through on both
 * sides one baseline holds two slots, the left one filled first.
 *
 * Line-by-line mirror of engine/psd-render/core/src/wrap_slots.rs; the search follows designcraft-compose
 * (`wrap_slot`, `free_slots`). Every constant and step is locked for TS↔Rust parity — never change one side alone.
 */

/** How text clears a zone: around its rectangle, around its polygon, or not beside it at all (jump below). */
export type WrapZoneMode = 'box' | 'contour' | 'jump';

/** Where text may go beside a zone; `largest` — the wider side of the frame. */
export type WrapZoneSide = 'both' | 'left' | 'right' | 'largest';

/** An object the text of a frame wraps around, in the frame's own space (canvas px from its top-left corner, y down). */
export interface WrapZone {
  /** Bounds the text keeps out of, offsets included. */
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
  readonly mode: WrapZoneMode;
  readonly side: WrapZoneSide;
  /** Contour wrap: the object's outline (closed polygon, offsets not included). */
  readonly contour?: readonly (readonly [number, number])[];
  /** Distance the text keeps from `contour`. */
  readonly contourOffset?: number;
}

/** Slack for float error when a line is tested against a jump zone's edges. */
export const WRAP_JUMP_EPS = 1e-3;

/** A segment narrower than this many font sizes beside an object stays empty (designcraft-compose `free_slots`). */
export const WRAP_MIN_SEGMENT_EM = 1.5;

/** Search steps a line may take down the frame before it gives up (hostile geometry). */
export const WRAP_MAX_STEPS = 4000;

/**
 * Whether any of `zones` crosses a frame `frameW` x `frameH` (its own space; a zone touching an edge does not): only
 * then the frame wraps. A frame whose objects sit beside, above or below it keeps its plain layout, vertical alignment
 * included; the height at layout time decides, so a frame grown into an object starts to wrap. Mirror of Rust
 * `zones_cross_frame`.
 */
export function zonesCrossFrame(zones: readonly WrapZone[], frameW: number, frameH: number): boolean {
  return zones.some(z => z.x0 < frameW && z.x1 > 0 && z.y0 < frameH && z.y1 > 0);
}

type Segment = [number, number];

function polygonBandExtent(points: readonly (readonly [number, number])[], y0: number, y1: number): Segment | null {
  let lo = Infinity;
  let hi = -Infinity;
  if (points.length === 0) return null;
  let p = points[points.length - 1];
  for (const q of points) {
    const a = p;
    const b = q;
    p = q;
    if (Math.max(a[1], b[1]) < y0 || Math.min(a[1], b[1]) > y1) continue;
    const dy = b[1] - a[1];
    let t0 = 0;
    let t1 = 1;
    if (Math.abs(dy) >= 1e-9) {
      const ta = (y0 - a[1]) / dy;
      const tb = (y1 - a[1]) / dy;
      t0 = Math.min(Math.max(Math.min(ta, tb), 0), 1);
      t1 = Math.min(Math.max(Math.max(ta, tb), 0), 1);
    }

    for (const t of [t0, t1]) {
      const x = a[0] + t * (b[0] - a[0]);
      lo = Math.min(lo, x);
      hi = Math.max(hi, x);
    }
  }

  return lo <= hi ? [lo, hi] : null;
}

/** Horizontal extent a zone blocks in the band `y0..y1`; `null` when it does not reach the band or is a jump zone. */
function zoneBandExtent(z: WrapZone, y0: number, y1: number): Segment | null {
  if (z.mode === 'jump' || !(z.y0 < y1 && z.y1 > y0)) return null;
  if (!z.contour) return [z.x0, z.x1];
  const off = Math.max(z.contourOffset ?? 0, 0);
  const ext = polygonBandExtent(z.contour, y0 - off, y1 + off);
  return ext ? [ext[0] - off, ext[1] + off] : null;
}

/**
 * Free segments of a frame `frameW` wide in the band `y0..y1` (a line's ink top to its baseline), left to right; empty
 * when the band is blocked. Once a zone cuts the band, segments narrower than {@link WRAP_MIN_SEGMENT_EM} of the
 * line's font `size` are dropped; a band no zone reaches is free however narrow.
 */
export function freeSlots(zones: readonly WrapZone[], frameW: number, y0: number, y1: number, size: number): Segment[] {
  let free: Segment[] = [[0, frameW]];
  let cut = false;
  for (const z of zones) {
    const ext = zoneBandExtent(z, y0, y1);
    if (!ext || !(ext[0] < frameW && ext[1] > 0)) continue;
    cut = true;
    let [a, b] = ext;
    // A one-sided wrap blocks the frame on the other side of the object too.
    const textLeft = z.side === 'left' ? true : z.side === 'right' ? false : z.side === 'largest' ? z.x0 >= frameW - z.x1 : null;
    if (textLeft === true) b = Math.max(b, frameW);
    else if (textLeft === false) a = Math.min(a, 0);
    const next: Segment[] = [];
    for (const [fa, fb] of free) {
      if (b <= fa || a >= fb) {
        next.push([fa, fb]);
        continue;
      }

      if (a > fa) next.push([fa, a]);
      if (b < fb) next.push([b, fb]);
    }

    free = next;
  }

  return cut ? free.filter(([a, b]) => b - a >= size * WRAP_MIN_SEGMENT_EM) : free;
}

/**
 * Line geometry the slots are searched with. Baselines are in the layout's baseline space (the first one at
 * `firstBaseline`); the frame y of a baseline is `baseline - seat`.
 */
export interface SlotMetrics {
  readonly frameW: number;
  readonly frameH: number;
  readonly seat: number;
  /**
   * Ascent of the {@link LineInk} of a line the wrap does not ask for (an empty forced line); a line asked for brings its
   * own.
   */
  readonly ascent: number;
  /** Size of that {@link LineInk}. */
  readonly size: number;
  readonly firstBaseline: number;
}

/**
 * How a line meets the zones: the height of its ink above the baseline (the band the zones are tested against) and the
 * font size the narrow-segment rule measures by.
 */
export interface LineInk {
  readonly ascent: number;
  readonly size: number;
}

/** The larger of each of two inks. */
export function maxInk(a: LineInk, b: LineInk): LineInk {
  return { ascent: Math.max(a.ascent, b.ascent), size: Math.max(a.size, b.size) };
}

/** One line of the wrap: its baseline (baseline space) and its segment of the frame. */
export interface Slot {
  readonly baseline: number;
  readonly x0: number;
  readonly width: number;
}

/** The forced lines of one paragraph, as the wrap meets them. */
export interface ParagraphShape {
  /** Forced lines (the paragraph cut at its soft returns). */
  readonly lines: number;
  /** Empty forced lines before its first non-empty one (all of them when it has none). */
  readonly leadEmpty: number;
  /** Space above the paragraph when it opens a baseline: space after the one above plus its own space before. */
  readonly gap: number;
}

/** The paragraphs of `text` (split at `\n`, lines at `soft`), `gap(p)` the space above paragraph `p`. */
export function paragraphShapes(text: string, soft: string, gap: (paragraph: number) => number): ParagraphShape[] {
  return text.split('\n').map((para, p) => {
    const parts = para.split(soft);
    const firstFull = parts.findIndex(s => s.length > 0);
    return { lines: parts.length, leadEmpty: firstFull < 0 ? parts.length : firstFull, gap: p === 0 ? 0 : gap(p) };
  });
}

/**
 * How a line clears a jump zone: its band reaches `top` above the baseline, and it moves down in whole `step`s from its
 * own place — or, with no `step` (the first line of the frame), right under the zone (designcraft-compose `Jump`).
 */
interface Jump {
  readonly top: number;
  readonly step: number;
}

/** Whether the segment `x0..x1` holds the first word of the line being placed. */
export type Holds = (x0: number, x1: number) => boolean;

/** Leading of the empty forced line `i`, which the wrap never asks for: that of the run holding its line break. */
export type EmptyLeading = (i: number) => number;

/** An empty line has no word to hold. */
const ANY_SEGMENT: Holds = () => true;

/**
 * Hands the wrap its slots in order: slot `i` is the segment line `i` sets. A paragraph opens a new baseline (the
 * segments left beside an object on the line above stay empty); a line inside a paragraph takes the next segment.
 *
 * The wrap asks for a line's width only when the line has text; an empty forced line (an empty paragraph, a soft return
 * on its own) takes its slot unasked. Those lines are rebuilt from the paragraph shapes: everything between the last
 * line asked for and the current one is empty forced lines.
 *
 * A segment that does not hold the line's first word stays empty, the line takes the next one that does — on its
 * baseline or below (designcraft-compose `holds`): a word is never cut to fit a sliver beside an object.
 */
export class SlotCursor {
  private readonly slots: Slot[] = [];
  /** Paragraph of the last line given a slot. */
  private paragraph: number | undefined;
  /** Segments left on the last baseline, the next first. */
  private pending: Segment[] = [];
  /** Ink of the line `pending` was found free for. */
  private pendingInk: LineInk = { ascent: 0, size: 0 };
  /** The zone bottom the search landed that line's ink top on (the search's `floor`). */
  private pendingFloor = -Infinity;
  private lastBaseline: number | undefined;

  constructor(
    private readonly zones: readonly WrapZone[],
    private readonly m: SlotMetrics,
    private readonly paragraphs: readonly ParagraphShape[],
  ) {}

  /**
   * The slot of line `i`, line of `paragraph`, `first` when it opens the paragraph; `leading` — the line's own leading
   * (its step from the baseline above), `ink` — how it meets the zones, `empty` — the leading of an empty line before
   * it, `holds` — whether a segment holds the line's first word.
   */
  slot(i: number, paragraph: number, first: boolean, leading: number, ink: LineInk, empty: EmptyLeading, holds: Holds): Slot {
    const known = this.slots[i];
    if (known) return known;
    this.fillMissing(i - this.slots.length, paragraph, !first, empty);
    const opens = first && (this.paragraph === undefined || paragraph > this.paragraph);
    this.push(opens ? paragraph : undefined, leading, ink, holds);
    return this.slots[i];
  }

  /** Slots of all `lines` lines, the empty ones after the last line asked for included. */
  finish(lines: number, empty: EmptyLeading): readonly Slot[] {
    if (lines > this.slots.length) this.fillMissing(lines - this.slots.length, this.paragraphs.length, false, empty);
    this.slots.length = Math.min(this.slots.length, lines);
    return this.slots;
  }

  /** Gives slots to the `k` empty lines before a line of `paragraph` (`inside` — not its first line). */
  private fillMissing(k: number, paragraph: number, inside: boolean, empty: EmptyLeading): void {
    if (k <= 0) return;
    const start = this.paragraph === undefined ? 0 : this.paragraph + 1;
    let opens: (number | undefined)[] = [];
    for (let q = start; q < Math.min(paragraph, this.paragraphs.length); q++) {
      opens.push(q);
      for (let n = 1; n < this.paragraphs[q].lines; n++) opens.push(undefined);
    }

    if (inside && paragraph >= start && paragraph < this.paragraphs.length) {
      const lead = this.paragraphs[paragraph].leadEmpty;
      if (lead > 0) {
        opens.push(paragraph);
        for (let n = 1; n < lead; n++) opens.push(undefined);
      }
    }

    // Not the shape the wrap walked: give the lines plain slots rather than misplace the rest.
    if (opens.length > k) opens = new Array<undefined>(k).fill(undefined);
    const tail = k - opens.length;
    const ink: LineInk = { ascent: this.m.ascent, size: this.m.size };
    for (let n = 0; n < tail; n++) this.push(undefined, empty(this.slots.length), ink, ANY_SEGMENT);
    for (const open of opens) this.push(open, empty(this.slots.length), ink, ANY_SEGMENT);
  }

  /** The next slot; `open` — the line opens that paragraph, `leading` and `ink` — the line's own. */
  private push(open: number | undefined, leading: number, ink: LineInk, holds: Holds): void {
    if (open !== undefined) {
      this.paragraph = open;
      this.pending = [];
    }

    // The segments left on the baseline that do not hold the word or are not free for the line stay empty; none left —
    // a new baseline.
    const left = this.lastBaseline ?? this.m.firstBaseline;
    const k = this.pending.findIndex(([a, b]) => holds(a, b) && this.freeFor(left, ink, a, b));
    if (k >= 0) {
      const [a, b] = this.pending[k];
      this.pending.splice(0, k + 1);
      this.slots.push({ baseline: left, x0: a, width: b - a });
      return;
    }

    this.pending = [];
    const wanted =
      this.lastBaseline === undefined
        ? this.m.firstBaseline
        : this.lastBaseline + leading + (open === undefined ? 0 : (this.paragraphs[open]?.gap ?? 0));
    // A jump zone keeps a following line's whole leading clear and moves it in whole leadings; the first line of the
    // frame clears it by its ink ascent and sits right under it.
    const following = this.lastBaseline !== undefined && leading > 0;
    const jump: Jump = following ? { top: leading, step: leading } : { top: ink.ascent, step: 0 };
    const [baseline, segs, floor] = this.search(wanted, jump, ink, holds);
    this.lastBaseline = baseline;
    const [a, b] = segs[0];
    this.pending = segs.slice(1);
    this.pendingInk = ink;
    this.pendingFloor = floor;
    this.slots.push({ baseline, x0: a, width: b - a });
  }

  /**
   * Whether the segment `a..b` left on `baseline` is free for a line of `ink`: a line taller or larger than the one that
   * found the segment tests it by its own band and size. Neither — free as found, not tested again. A line no taller has
   * its band top no higher than the search's `floor`, as the search measured it.
   */
  private freeFor(baseline: number, ink: LineInk, a: number, b: number): boolean {
    if (ink.ascent <= this.pendingInk.ascent && ink.size <= this.pendingInk.size) return true;
    const bf = baseline - this.m.seat;
    const y0 = ink.ascent <= this.pendingInk.ascent ? Math.max(bf - ink.ascent, this.pendingFloor) : bf - ink.ascent;
    return freeSlots(this.zones, this.m.frameW, y0, bf, ink.size).some(([fa, fb]) => fa <= a && b <= fb);
  }

  /**
   * The first baseline at or below `wanted` with a free segment that `holds` the line's first word (the segments before
   * it stay empty; never empty). A line whose `jump.top`..baseline reaches a jump zone moves below it ({@link Jump});
   * elsewhere its band is `ink.ascent`..baseline, cut by the rule of `ink.size`: beside a rectangle the search jumps to
   * the bottom of the zone that blocks it — the line's ink top exactly on it —, beside a contour 1 px at a time. Nothing
   * found above the frame bottom — the overflow line: the whole frame width, at the frame bottom or below it. Also returns
   * the `floor` the found band was measured with.
   */
  private search(wanted: number, jump: Jump, ink: LineInk, holds: Holds): [number, Segment[], number] {
    const m = this.m;
    const top = jump.top;
    let b = wanted;
    // The zone bottom the last step landed the ink top on: `bf - ink.ascent` there may round a hair above it, and the
    // zone would block the band again.
    let floor = -Infinity;
    for (let step = 0; step < WRAP_MAX_STEPS; step++) {
      const bf = b - m.seat;
      let jumped = -Infinity;
      for (const z of this.zones) {
        if (z.mode !== 'jump' || !(z.x0 < m.frameW && z.x1 > 0)) continue;
        // Boundary, unverified in InDesign: a fixed-type first baseline at the frame top ignores a jump zone from there.
        if (bf > z.y0 + WRAP_JUMP_EPS && bf - top < z.y1 - WRAP_JUMP_EPS) jumped = Math.max(jumped, z.y1);
      }

      if (jumped > -Infinity) {
        const from = wanted - m.seat;
        b =
          jump.step > 0
            ? wanted + Math.max(Math.ceil((jumped + top - from - WRAP_JUMP_EPS / 2) / jump.step), 0) * jump.step
            : jumped + top + m.seat;
        floor = -Infinity;
      } else {
        const y0 = Math.max(bf - ink.ascent, floor);
        const y1 = bf;
        const free = freeSlots(this.zones, m.frameW, y0, y1, ink.size);
        const k = free.findIndex(([a, c]) => holds(a, c));
        if (k >= 0) return [b, free.slice(k), floor];
        let bottom = Infinity;
        let contour = false;
        for (const z of this.zones) {
          if (z.mode === 'jump' || !(z.y0 < y1 && z.y1 > y0)) continue;
          bottom = Math.min(bottom, z.y1);
          contour ||= z.contour != null;
        }

        const edge = bottom + ink.ascent;
        const next = contour ? Math.min(edge, bf + 1) : edge;
        if (next <= bf || !Number.isFinite(next)) break;
        floor = next === edge ? bottom : -Infinity;
        b = next + m.seat;
      }

      if (Number.isNaN(b) || b - m.seat > m.frameH) break;
    }

    const bottom = m.frameH + m.seat;
    return [Number.isFinite(b) ? Math.max(b, bottom) : Math.max(wanted, bottom), [[0, m.frameW]], -Infinity];
  }
}
